// pages/Admins.tsx
import React from "react";
import { Helmet } from "react-helmet-async";
import AddAdminModal from "../components/admin/AddAdminModal";
import Button from "../components/Button";
import ConfirmationDialog from "../components/ConfirmationDialog";
import DataTableView from "../components/DataTableView2";
import Input from "../components/Input";
import { IGetAdminsDocs } from "../utils/types";
import useAdmins from '../hooks/logics/management/use-admin-dashboard';

const userTypeMap: Record<string, string> = {
  admin: "Admin",
  super_admin: "Super Admin",
};

const Admins = () => {
  const {
    admins,
    totalPages,
    isFetching,
    isDeletingAdmin,
    search,
    setSearch,
    selectedAdminId,
    setSelectedAdminId,
    isAddAdminOpen,
    setIsAddAdminOpen,
    user,
    isAdmin,
    searchParams,
    updateUrlParams,
    handleSearch,
    handleDeleteAdmin,
    refetch,
  } = useAdmins();

  const columns = [
    {
      header: "First name",
      className: "md:p-0 md:pl-10",
      accessor: (admin: IGetAdminsDocs) => (
        <div className="md:!pl-7">{admin.firstName || "N/A"}</div>
      ),
    },
    {
      header: "Last name",
      accessor: (admin: IGetAdminsDocs) => admin.lastName || "N/A",
    },
    {
      header: "Email",
      accessor: (admin: IGetAdminsDocs) => admin.username || "N/A",
    },
    {
      header: "User type",
      accessor: (admin: IGetAdminsDocs) => userTypeMap[admin.userType] || "N/A",
    },
    {
      header: "Actions",
      accessor: (admin: IGetAdminsDocs) =>
        admin._id === user?._id ? "N/A" : (
          <button
            onClick={() => setSelectedAdminId(admin._id)}
            className="text-red-500"
          >
            Delete
          </button>
        ),
    },
  ].filter((col) => !(isAdmin && col.header === "Actions"));

  return (
    <>
      <Helmet>
        <title>Admins</title>
      </Helmet>
      <div className="w-full h-full">
        <div className="pb-4 flex flex-col justify-center font-inter bg-gray-100">
          <form onSubmit={handleSearch} className="w-full flex flex-col md:flex-row gap-4">
            <div className="w-full flex flex-row gap-x-2 md:gap-x-4 md:justify-end">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search"
                containerClassName="w-2/3 h-full md:w-auto"
                outerClassName="h-full"
                className="w-full md:w-52 outline-none input-primary h-8 md:h-10 rounded-lg"
              />
              <Button disabled={!search} type="submit" className="w-1/3 md:w-32 min-h-0 h-8 md:h-10">
                Search
              </Button>
            </div>
            <div className="w-full md:w-min flex flex-row gap-x-2 md:gap-x-4 md:justify-end">
              <Button
                disabled={!search}
                type="button"
                onClick={() => { setSearch(""); updateUrlParams({ search: "", page: "1" }); }}
                className="flex-1 md:flex-none md:w-32 min-h-0 h-8 md:h-10"
              >
                Clear
              </Button>
              <Button onClick={() => setIsAddAdminOpen(true)} className="flex-1 md:flex-none md:w-32 min-h-0 h-8 md:h-10">
                Add admin
              </Button>
            </div>
          </form>
        </div>

        <DataTableView
          data={admins}
          isFetching={isFetching}
          emptyStateMessage="No admin found"
          columns={columns}
          totalPages={totalPages}
          currentPage={parseInt(searchParams.get("page") || "1")}
          setCurrentPage={(page: number) => updateUrlParams({ page: page.toString() })}
        />
      </div>

      <ConfirmationDialog
        handleClick={handleDeleteAdmin}
        isSubmitButtonDisabled={isDeletingAdmin}
        isSubmitting={isDeletingAdmin}
        isVisible={selectedAdminId !== ""}
        onHide={() => setSelectedAdminId("")}
        text="Are you sure you want to delete this admin?"
      />

      <AddAdminModal
        onSuccess={refetch}
        isVisible={isAddAdminOpen}
        onHide={() => setIsAddAdminOpen(false)}
      />
    </>
  );
};

export default Admins;